import { useNavigate, useParams } from 'react-router';
import { useInvalidate, useOne } from '@refinedev/core';
import { humanizeEnum } from '../../../components/Form.js';
import { DocumentList } from '../../../components/DocumentList.js';
import { PhotoUpload } from '../../../components/PhotoUpload.js';

interface MaintenanceRequestRecord {
  id: string;
  unitLabel: string;
  propertyName: string;
  title: string;
  description: string | null;
  status: string;
  scheduledOn: string | null;
  completedAt: string | null;
  createdAt: string | null;
}

/**
 * Track one maintenance request: status and a few key dates. Deliberately no
 * `WorkOrderNote` feed here — that's the staff/vendor coordination log, and
 * nothing marks a note tenant-safe (see rls-policies.sql's `work_orders`
 * comment). A tenant sees what they submitted and where it stands, not the
 * internal back-and-forth.
 *
 * Photo upload is gated on `status === 'OPEN'` as UX, not security — RLS
 * (`documents_tenant_insert`, scoped through `app_tenant_owns_work_order`)
 * is what actually enforces "only the request's own reporter, and only their
 * own work order." Uploading is meant as a one-time step right after
 * submitting, not an ongoing feature to revisit.
 */
export function MaintenanceRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidate = useInvalidate();

  const { data, isLoading } = useOne<MaintenanceRequestRecord>({
    resource: 'work-orders',
    id: id ?? '',
    queryOptions: { enabled: Boolean(id) },
  });

  const request = data?.data;

  if (isLoading) return <p className="muted">Loading…</p>;
  if (!request) return <p className="alert alert--error">Request not found.</p>;

  return (
    <section className="stack">
      <div className="row">
        <h1>{request.title}</h1>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/portal/tenant/maintenance')}
        >
          ← All requests
        </button>
      </div>

      <div className="card stack">
        <div className="row">
          <span className="pill">{humanizeEnum(request.status)}</span>
          <span className="muted">
            {request.unitLabel}, {request.propertyName}
          </span>
        </div>
        {request.description ? <p>{request.description}</p> : null}
        <p className="muted">
          Submitted {request.createdAt ? request.createdAt.slice(0, 10) : '—'}
          {request.scheduledOn ? ` · scheduled ${request.scheduledOn}` : ''}
          {request.completedAt ? ` · completed ${request.completedAt.slice(0, 10)}` : ''}
        </p>
      </div>

      <h2>Photos</h2>
      <DocumentList filter={{ workOrderId: request.id }} emptyMessage="No photos attached yet." />
      {request.status === 'OPEN' ? (
        <div className="card">
          <PhotoUpload
            parent={{ workOrderId: request.id }}
            defaultDocumentType="INSPECTION_PHOTO"
            simple
            imageOnly
            onUploaded={() => {
              void invalidate({ resource: 'documents', invalidates: ['list'] });
            }}
          />
        </div>
      ) : null}
    </section>
  );
}
